import { useQueryClient } from '@tanstack/react-query';
import { LoadingAnimation, toastNotifications, UploadedFile, FileUpload } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentType, OnboardingRequestModel } from 'api/models';
import {
    getGetAttachmentsQueryKey,
    useDeleteAttachmentContent,
    useDownloadAttachmentContentHook,
    useGetAttachments,
    useUploadAttachmentContentByType,
} from 'api/queries/attachments/attachments';
import { useDownloadContractFileContentHook, useGetSignedContractFiles } from 'api/queries/contracts/contracts';
import { downloadFileWithHook } from 'utils';
import { CountryRule } from '../CompanyForm/CountryRule';
import { RequestAttachment } from './RequestAttachment';

type Props = {
    request: OnboardingRequestModel;
    mode: 'Frontend' | 'Backoffice';
};
const acceptFileType = 'application/pdf,image/png,image/jpeg,image/tiff,image/heif,image/heic';

export const RequestAttachments = ({ request, mode }: Props) => {
    const { data: attachmentsResponse, isPending: areAttachmentsPending } = useGetAttachments(request.id ?? '');
    const { data: contractsResponse, isPending: areContractsPending } = useGetSignedContractFiles(request.id ?? '');
    const attachments = attachmentsResponse?.data ?? [];
    const contracts = contractsResponse?.data ?? [];
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const downloadContractFileContent = useDownloadContractFileContentHook();

    const downloadAttachmentContent = useDownloadAttachmentContentHook();
    const uploadAttachment = useUploadAttachmentContentByType();
    const deleteAttachment = useDeleteAttachmentContent();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [uploadedFiles, setUploadedFiles] = useState<Record<AttachmentType, UploadedFile | null>>({} as any);

    const countryRule = CountryRule.FindById(request.companyData?.mainAddress?.countryConfiguration?.id ?? '');

    const handleFileChange = (type: AttachmentType) => async (file: UploadedFile | null) => {
        if (!file) {
            return;
        }

        const existing = attachments.find((a) => a.attachmentType === type && a.exists);
        try {
            if (existing?.id) {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                await deleteAttachment.mutateAsync({ id: request.id!, attachmentId: existing.id });
                await queryClient.invalidateQueries({ queryKey: getGetAttachmentsQueryKey(request.id) });
            }

            await uploadAttachment.mutateAsync({
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                id: request.id!,
                attachmentType: type,
                data: { File: file.file },
            });
            await queryClient.invalidateQueries({ queryKey: getGetAttachmentsQueryKey(request.id) });

            setUploadedFiles((prev) => ({ ...prev, [type]: file }));
            toastNotifications.success({ title: t('success'), message: t('documentUploaded') });
        } catch (err) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                },
                err,
            );
        }
    };

    const handleFileRemove = (type: AttachmentType) => async () => {
        const existing = attachments.find((a) => a.attachmentType === type && a.exists);
        if (!existing?.id) {
            return;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            await deleteAttachment.mutateAsync({ id: request.id!, attachmentId: existing.id });
            setUploadedFiles((prev) => ({ ...prev, [type]: null }));
            await queryClient.invalidateQueries({ queryKey: getGetAttachmentsQueryKey(request.id) });
            toastNotifications.success({ title: t('success'), message: t('documentDeleted') });
        } catch (err) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                },
                err,
            );
        }
    };

    const getDisplayedFile = (type: AttachmentType): { name: string; size?: number; blob?: Blob }[] => {
        const uploaded = uploadedFiles[type];
        if (uploaded) {
            return [{ name: uploaded.file.name, size: uploaded.file.size, blob: uploaded.file }];
        }

        const existing = attachments.find((a) => a.attachmentType === type && a.exists);
        if (existing) {
            return [
                {
                    name: existing.fileName ?? '',
                    size: existing.fileSizeBytes ?? undefined,
                },
            ];
        }

        return [];
    };

    const handleAttachmentDownload = async (attachmentId: string, attachmentName: string) => {
        try {
            await downloadFileWithHook(downloadAttachmentContent(request.id, attachmentId), attachmentName);
        } catch (err) {
            toastNotifications.errorDetail(
                {
                    title: t('errorDownloadingAttachment'),
                },
                err,
            );
        }
    };

    const handleContractDownload = async (attachmentId: string, attachmentName: string) => {
        try {
            await downloadFileWithHook(downloadContractFileContent(request.id, attachmentId), attachmentName);
        } catch (err) {
            toastNotifications.errorDetail(
                {
                    title: t('errorDownloadingContract'),
                },
                err,
            );
        }
    };

    const attachmentConfigs = [
        {
            type: AttachmentType.BusinessLicense,
            fieldKey: 'businessLicenseUpload',
        },
        {
            type: AttachmentType.CommercialRegistration,
            fieldKey: 'commercialRegistrationUpload',
        },
        {
            type: AttachmentType.VATCertificate,
            fieldKey: 'VATCertificateUpload',
        },
        {
            type: AttachmentType.RegisteredNationalAddress,
            fieldKey: 'RegisteredNationalAddressUpload',
        },
    ] as const;

    attachmentConfigs.forEach(({ type }) => {
        const alreadyLoaded = uploadedFiles[type];
        const existing = attachments.find((a) => a.attachmentType === type && a.exists);

        if (!alreadyLoaded && existing?.id) {
            downloadAttachmentContent(request.id, existing.id).then((res) => {
                const blob = res as Blob;
                if (!existing?.id) {
                    return;
                }
                const fileObj: UploadedFile = {
                    file: new File([blob], existing.fileName ?? 'document', { type: blob.type }),
                    id: existing.id,
                };
                setUploadedFiles((prev) => ({ ...prev, [type]: fileObj }));
            });
        }
    });

    return (
        <>
            {attachments.some((attachment) => attachment.exists) && <div className="text-base">{t('documents')}</div>}
            <div className="gap-base grid grid-cols-1">
                {mode === 'Backoffice'
                    && !areAttachmentsPending
                    && !areContractsPending
                    && request.currentState === 'DataVerification'
                    && attachmentConfigs.map(({ type, fieldKey }) => {
                        const fieldConfig = countryRule?.fieldConfigurations?.[fieldKey];

                        if (!fieldConfig?.visible) {
                            return null;
                        }

                        return (
                            <div key={type} className="gap-sm flex flex-col">
                                <FileUpload
                                    accept={acceptFileType}
                                    files={getDisplayedFile(type)}
                                    label={t(type)}
                                    mode="single"
                                    required={fieldConfig.mandatory}
                                    onFileSelected={handleFileChange(type)}
                                    onRemoveFile={handleFileRemove(type)}
                                />
                            </div>
                        );
                    })}

                {(areAttachmentsPending || areContractsPending) && <LoadingAnimation />}
                {!areAttachmentsPending
                    && !areContractsPending
                    && ((mode === 'Backoffice' && request.currentState !== 'DataVerification')
                        || mode === 'Frontend') && (
                        <>
                            {attachments
                                && attachments
                                    .filter((attachment) => attachment.exists)
                                    .map((attachment) => (
                                        <RequestAttachment
                                            key={attachment.id}
                                            attachmentType={attachment.attachmentType}
                                            fileName={attachment.fileName}
                                            fileSizeBytes={attachment.fileSizeBytes}
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            id={attachment.id!}
                                            onClick={() =>
                                                handleAttachmentDownload(
                                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                    attachment.id!,
                                                    attachment.fileName ?? 'unknown_file',
                                                )
                                            }
                                        />
                                    ))}
                            {contracts
                                && contracts
                                    .filter((contract) => contract.exists)
                                    .map((contract) => (
                                        <RequestAttachment
                                            key={contract.id}
                                            attachmentType={contract.attachmentType}
                                            fileName={contract.fileName}
                                            fileSizeBytes={contract.fileSizeBytes}
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            id={contract.id!}
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            onClick={() => handleContractDownload(contract.id!, contract.fileName!)}
                                        />
                                    ))}
                        </>
                    )}
            </div>
        </>
    );
};
